'use client';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { AlertCircle, Check, Clock, Loader2, Play, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { Button } from '@repo/ui/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';
import { cn } from '@repo/ui/lib/utils';

import { YT_ANALYSIS_STATE } from '@/@types/youtube-analysis.types';
import { useYoutubeAnalysisWs } from '@/providers/youtube-analysis-ws-provider';

const TOOLTIP_DELAY_DURATION = 200;

/**
 * YouTube Analysis Indicator
 *
 * A hanging indicator that appears below the header when a YouTube video
 * analysis is in progress. Allows users to:
 * - See the current analysis state
 * - Click to navigate to the analysis page
 * - Dismiss the indicator
 */
export const YouTubeAnalysisIndicator: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { analysisState, clearAnalysis, isAnalysisActive, isAnalysisComplete } =
    useYoutubeAnalysisWs();

  // Don't show on the YouTube analysis page itself
  const isOnYouTubePage = pathname === '/videos/youtube';

  // Determine if indicator should be visible
  const shouldShow = useMemo(() => {
    if (isOnYouTubePage) return false;
    if (analysisState.state === YT_ANALYSIS_STATE.IDLE) return false;

    // Hide during check-only loading states (CONNECTING/REQUESTING/ERROR/TIMEOUT when isCheckOnly is true)
    const isCheckOnly = analysisState.isCheckOnly ?? false;
    if (
      isCheckOnly &&
      (analysisState.state === YT_ANALYSIS_STATE.CONNECTING ||
        analysisState.state === YT_ANALYSIS_STATE.REQUESTING)
    ) {
      return false;
    }

    // Show for active states, completed, error, or timeout
    return (
      isAnalysisActive ||
      isAnalysisComplete ||
      analysisState.state === YT_ANALYSIS_STATE.ERROR ||
      analysisState.state === YT_ANALYSIS_STATE.TIMEOUT
    );
  }, [
    isOnYouTubePage,
    analysisState.state,
    analysisState.isCheckOnly,
    isAnalysisActive,
    isAnalysisComplete,
  ]);

  const [isStateTextTooltipOpen, setIsStateTextTooltipOpen] = useState(false);

  const stateTextTooltipRef = useRef<HTMLSpanElement>(null);

  const handleStateTextTooltipOpenChange = useCallback((open: boolean) => {
    if (stateTextTooltipRef.current) {
      const isClamped =
        stateTextTooltipRef.current.scrollHeight >
        stateTextTooltipRef.current.clientHeight;
      setIsStateTextTooltipOpen(open && isClamped);
    }
  }, []);

  // Navigate to analysis page
  const handleClick = useCallback(() => {
    router.push('/videos/youtube');
  }, [router]);

  // Dismiss indicator
  const handleDismiss = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      clearAnalysis();
    },
    [clearAnalysis],
  );

  // Get state-specific UI elements
  const stateConfig = useMemo(() => {
    const isCheckOnly = analysisState.isCheckOnly ?? false;

    switch (analysisState.state) {
      case YT_ANALYSIS_STATE.CONNECTING:
        return {
          icon: (
            <Loader2 className="shrink-0 size-4 animate-spin text-blue-400" />
          ),
          text: isCheckOnly ? 'Checking analysis...' : 'Connecting...',
          canClick: false,
        };
      case YT_ANALYSIS_STATE.REQUESTING:
        return {
          icon: (
            <Loader2 className="shrink-0 size-4 animate-spin text-blue-400" />
          ),
          text: isCheckOnly ? 'Checking analysis...' : 'Starting analysis...',
          canClick: false,
        };
      case YT_ANALYSIS_STATE.ANALYZING:
        return {
          icon: (
            <Loader2 className="shrink-0 size-4 animate-spin text-amber-400" />
          ),
          text: 'Analyzing video...',
          canClick: false,
        };
      case YT_ANALYSIS_STATE.COMPLETED:
        return {
          icon: <Check className="shrink-0 size-4 text-emerald-400" />,
          text: 'Analysis ready!',
          canClick: true,
        };
      case YT_ANALYSIS_STATE.ERROR:
        return {
          icon: <AlertCircle className="shrink-0 size-4 text-red-400" />,
          text: analysisState.error || 'Analysis failed',
          canClick: false,
        };
      case YT_ANALYSIS_STATE.TIMEOUT:
        return {
          icon: <Clock className="shrink-0 size-4 text-orange-400" />,
          text: 'Analysis timed out',
          canClick: false,
        };
      default:
        return {
          icon: null,
          text: '',
          canClick: false,
        };
    }
  }, [analysisState.isCheckOnly, analysisState.state, analysisState.error]);

  // Get video title or fallback
  const displayTitle = useMemo(() => {
    if (analysisState.data?.title) {
      // Truncate long titles
      return analysisState.data.title.length > 40
        ? `${analysisState.data.title.substring(0, 40)}...`
        : analysisState.data.title;
    }
    return 'YouTube Video';
  }, [analysisState.data?.title]);

  return (
    <AnimatePresence mode="wait">
      {shouldShow && (
        <motion.div
          initial={{ opacity: 0, y: -10, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: -10, height: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="sticky top-[39px] cred-md:top-[46px] cred-lg:top-[49px] cred-xl:top-[64px] z-10 overflow-hidden"
        >
          <div
            role={stateConfig.canClick ? 'button' : 'status'}
            tabIndex={stateConfig.canClick ? 0 : undefined}
            onClick={stateConfig.canClick ? handleClick : undefined}
            onKeyDown={(e) => {
              if (
                stateConfig.canClick &&
                (e.key === 'Enter' || e.key === ' ')
              ) {
                handleClick();
              }
            }}
            className={cn(
              'flex items-center gap-2.5 px-3 pt-[7px] pb-1 border-b transition-colors h-[51px] cred-md:h-[59px] cred-lg:h-[59px] cred-xl:h-[71px]',
              'cred-md:gap-3 cred-md:px-3.5 cred-md:pt-[9px] cred-md:pb-1.5',
              'cred-lg:px-4',
              'cred-xl:px-5 cred-xl:pt-[11px] cred-xl:pb-2 bg-[#F0F2F5]',
              stateConfig.canClick && 'cursor-pointer hover:bg-[#0A2342]/20',
            )}
          >
            {/* Thumbnail or Play icon */}
            <div className="relative shrink-0 size-8 rounded overflow-hidden bg-black/30 cred-md:size-9 cred-xl:size-11">
              {analysisState.thumbnailUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={analysisState.thumbnailUrl}
                  alt="Channel Thumbnail"
                  className="size-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center size-full">
                  <Play className="size-4 text-[#2979FF]" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-black truncate cred-md:text-sm cred-xl:text-base">
                {displayTitle}
              </p>
              <div className="flex items-center gap-1.5 mt-px">
                {stateConfig.icon}
                <TooltipProvider>
                  <Tooltip
                    delayDuration={TOOLTIP_DELAY_DURATION}
                    open={isStateTextTooltipOpen}
                    onOpenChange={handleStateTextTooltipOpenChange}
                  >
                    <TooltipTrigger asChild>
                      <span
                        ref={stateTextTooltipRef}
                        title={stateConfig.text}
                        className="text-[10px] leading-3 text-black line-clamp-2 cred-md:text-xs cred-md:leading-3.5 cred-xl:text-sm cred-xl:leading-4"
                      >
                        {stateConfig.text}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-72 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] cred-md:max-w-80 cred-lg:max-w-[330px] cred-xl:max-w-[440px]">
                      <p className="text-sm leading-3.5 text-[#292929] cred-md:text-[15px] cred-md:leading-[17.5px] cred-lg:text-base cred-lg:leading-[19px] cred-xl:text-lg">
                        {stateConfig.text}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Progress bar for analyzing state */}
            {analysisState.state === YT_ANALYSIS_STATE.ANALYZING && (
              <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-black/10">
                <motion.div
                  className="h-full bg-[#2979FF]"
                  initial={{ width: '0%' }}
                  animate={{ width: `${analysisState.progress || 0}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            )}

            {/* View button for completed state */}
            {isAnalysisComplete && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClick}
                className="shrink-0 h-7 px-2.5 text-xs bg-[#2979FF] text-white hover:bg-[#2979FF]/90 hover:text-white cred-md:h-8 cred-md:px-3 cred-xl:h-9"
              >
                View
              </Button>
            )}

            {/* Dismiss button */}
            <Button
              size="icon"
              variant="ghost"
              onClick={handleDismiss}
              className="shrink-0 size-6 text-black/50 hover:text-black hover:bg-black/10 cred-md:size-7 cred-xl:size-8 [&_svg]:size-3.5 cred-md:[&_svg]:size-4"
            >
              <X />
              <span className="sr-only">Dismiss</span>
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
